import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    FaSync,
    FaCheck,
    FaTimes,
    FaSpinner,
    FaExclamationTriangle,
    FaInfoCircle,
    FaCalendarAlt,
    FaExternalLinkAlt,
    FaChevronDown,
    FaChevronUp,
    FaCar,
    FaShieldAlt,
    FaFileAlt,
    FaUserCheck,
    FaArrowRight,
    FaCheckCircle,
} from 'react-icons/fa';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import {
    stageFullProfile,
    approveFullProfile,
    getOnboardingCrossDivisionReviewScope,
} from '../../services/onboardingService';
import ProfileRevertControls from './ProfileRevertControls';
import CrossDivisionScopeModal from './CrossDivisionScopeModal';

const FIELD_LABELS = {
    'secondary_email': 'Secondary email',
    'fullName': 'Full name',
    'fullName_title': 'Title',
    'gender': 'Gender',
    'secondaryTel': 'Secondary telephone',
    'dob': 'Date of birth',
    'address.firstLineOfAddress': 'Address line 1',
    'address.secondLineOfAddress': 'Address line 2',
    'address.town': 'Town / city',
    'address.county': 'County',
    'address.postalCode': 'Postcode',
    'address.region': 'Region',
    'nextOfKin.name': 'Contact name',
    'nextOfKin.relation': 'Relationship',
    'nextOfKin.phone': 'Contact telephone',
    'driversLicense.licenseNumber': 'Driving licence number',
    'driversLicense.expiryDate': 'Licence expiry date',
    'driving_details.driver': 'Declared driver',
    'driving_details.business_car_insurance': 'Business insurance declaration',
    'carInsurance.file': 'Insurance certificate',
    'declarations_and_agreements.registration_final_declaration': 'Final declaration',
    'declarations_and_agreements.declarations_agreed_at': 'Declaration accepted on',
    'declarations_and_agreements.agrees_to_terms_and_conditions': 'Terms accepted',
    'declarations_and_agreements.weekly_work_limit': 'Weekly working limit declaration',
    'terms_of_engagement.terms_printed_name': 'Signed name',
    'terms_of_engagement.terms_agreed_at': 'Terms signed on',
    'proofs_of_identity_and_address.proof_of_address_1_url': 'Address proof 1',
    'proofs_of_identity_and_address.proof_of_address_2_url': 'Address proof 2',
    'proofs_of_identity_and_address.proof_of_id_url': 'Identity proof',
    'proof_of_id_url': 'Identity proof — employee shortcut',
    'address.proof_of_address_1_url': 'Address proof 1 — address shortcut',
    'address.proof_of_address_2_url': 'Address proof 2 — address shortcut',
    'safeguarding.safeguarding_disqualified': 'Disqualification declaration',
    'safeguarding.safeguarding_disqualified_details': 'Disqualification details',
    'safeguarding.safeguarding_dismissed': 'Dismissal declaration',
    'safeguarding.safeguarding_dismissed_details': 'Dismissal details',
    'residency_and_police_checks.residency_history_details': 'Residency history',
    'residency_and_police_checks.has_possession_of_police_checks': 'Police checks held',
    'residency_and_police_checks.has_continuous_overseas_residency': 'Continuous overseas residency',
    'residency_and_police_checks.police_check_document_urls': 'Police check documents',
    'residency_and_police_checks.has_continuous_uk_residency': 'Continuous UK residency',
    'onboardingIntegration.agencies': 'Agencies',
    'onboardingIntegration.candidateStatus': 'Onboarding360 status',
    'onboardingIntegration.sourceCreatedAt': 'Onboarding360 account created',
    'onboardingIntegration.lastCandidateUpdateAt': 'Profile last updated',
    'onboardingIntegration.lastComplianceUpdateAt': 'Compliance last updated',
    'employeeDetails.onboardingUserID': 'Onboarding360 identity link',
    'onboardingIntegration.externalUserID': 'Integration identity link',
    'driversLicense.dvla': 'DVLA validation',
};

const getFieldLabel = (path) => {
    if (FIELD_LABELS[path]) return FIELD_LABELS[path];
    const lastPart = path.split('.').pop();
    return lastPart
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^\w/, (c) => c.toUpperCase());
};

const formatValueDisplay = (val) => {
    if (val === null || val === undefined || val === '') return 'Not recorded';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (Array.isArray(val)) {
        if (val.length === 0) return 'Not recorded';
        if (val.every((v) => typeof v === 'string')) return val.join(', ');
        return JSON.stringify(val, null, 2);
    }
    if (typeof val === 'object') return JSON.stringify(val, null, 2);
    return String(val);
};

const FullProfileReview = ({
    serverID,
    purgatoryID,
    employeeID,
    item,
    onReject,
    disabled = false,
}) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [report, setReport] = useState(null);
    const [selectedPaths, setSelectedPaths] = useState(new Set());
    const [dateChoices, setDateChoices] = useState({});
    const [showMetadata, setShowMetadata] = useState(false);
    const [revertRefreshKey, setRevertRefreshKey] = useState(0);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [crossDivisionScope, setCrossDivisionScope] = useState(null);
    const [checkingScope, setCheckingScope] = useState(false);
    const [openSections, setOpenSections] = useState({ Personal: true });
    const [openDetails, setOpenDetails] = useState({ attention: true });

    // Live validation alerts on the employee record (DVLA & Insurance)
    const [employeeAlerts, setEmployeeAlerts] = useState([]);

    useEffect(() => {
        if (!serverID || !employeeID) return;
        const employeeRef = doc(db, 'servers', serverID, 'employees', employeeID);
        const unsub = onSnapshot(employeeRef, (snap) => {
            if (!snap.exists()) return;
            const data = snap.data();
            const alerts = [];
            const dvla = data?.driversLicense?.dvla;
            if (dvla && dvla.validationRequired === true && dvla.validated !== true) {
                alerts.push('Licence number changed — DVLA validation required.');
            }
            const insurance = data?.carInsurance;
            if (insurance && insurance.validationRequired === true && insurance.approvedByAdmin !== true) {
                alerts.push('New insurance evidence — policy validation required.');
            }
            setEmployeeAlerts(alerts);
        }, (err) => {
            console.error('Error listening to employee validation alerts:', err);
        });
        return () => unsub();
    }, [serverID, employeeID]);

    // Reset local state if candidate changes
    useEffect(() => {
        setReport(null);
        setSelectedPaths(new Set());
        setDateChoices({});
        setError(null);
        setSuccessMessage(null);
    }, [serverID, purgatoryID, employeeID]);

    const handleStage = async () => {
        if (busy || disabled) return;
        setBusy(true);
        setError(null);
        setSuccessMessage(null);
        setReport(null);
        setSelectedPaths(new Set());
        setDateChoices({});

        try {
            const res = await stageFullProfile(serverID, purgatoryID);
            if (res?.success !== true || (employeeID && res?.employeeID && res.employeeID !== employeeID)) {
                throw new Error('The matched employee changed or staging failed. Refresh the page.');
            }
            setReport(res);

            // Select mandatory or selectedByDefault fields
            const initialSelected = new Set();
            const initialDates = {};
            const changes = res?.plan?.changes || [];
            changes.forEach((row) => {
                if (row.mandatory === true || row.selectedByDefault === true) {
                    initialSelected.add(String(row.target));
                }
                if (row.type === 'date' && row.choices && row.choices.length > 0) {
                    initialDates[String(row.target)] = row.choices[0].value;
                }
            });
            setSelectedPaths(initialSelected);
            setDateChoices(initialDates);
        } catch (err) {
            console.error('Failed to stage full profile:', err);
            setError(err?.message || 'Failed to stage profile changes.');
        } finally {
            setBusy(false);
        }
    };

    const handleTogglePath = (path) => {
        if (busy) return;
        setSelectedPaths((prev) => {
            const next = new Set(prev);
            if (next.has(path)) {
                next.delete(path);
            } else {
                next.add(path);
            }
            return next;
        });
    };

    const handleDateChange = (path, value) => {
        setDateChoices((prev) => ({ ...prev, [path]: value }));
        setSelectedPaths((prev) => {
            const next = new Set(prev);
            next.add(path);
            return next;
        });
    };

    const toggleSection = (section) => {
        setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
    };

    const plan = report?.plan || {};
    const changes = plan.changes || [];
    const issues = plan.issues || [];
    const unchanged = plan.unchanged || [];
    const skipped = plan.skipped || [];

    const editableChanges = useMemo(() => {
        return changes.filter((r) => r.mandatory !== true);
    }, [changes]);

    const attentionItems = useMemo(() => {
        return editableChanges.filter((r) => Boolean(r.note && String(r.note).trim().length > 0));
    }, [editableChanges]);

    const visibleChanges = useMemo(() => {
        if (showMetadata) return editableChanges;
        return editableChanges.filter((r) => r.section !== 'Provider metadata');
    }, [editableChanges, showMetadata]);

    const groupedChanges = useMemo(() => {
        const groups = {};
        visibleChanges.forEach((row) => {
            const sec = row.section || 'General';
            if (!groups[sec]) groups[sec] = [];
            groups[sec].push(row);
        });
        return groups;
    }, [visibleChanges]);

    const selectedCount = useMemo(() => {
        return editableChanges.filter((r) => selectedPaths.has(String(r.target))).length;
    }, [editableChanges, selectedPaths]);

    const handlePreApprove = async () => {
        if (busy || checkingScope || !report) return;

        // Check if any selected date field is missing a date choice
        const selectedDateMissing = editableChanges.some((r) => {
            const path = String(r.target);
            return selectedPaths.has(path) && r.type === 'date' && !dateChoices[path];
        });

        if (selectedDateMissing) {
            setError('Please choose a calendar date for each selected date field.');
            return;
        }

        setCheckingScope(true);
        setError(null);

        try {
            const scopeRes = await getOnboardingCrossDivisionReviewScope({
                serverID,
                purgatoryID,
                proposalID: report.proposalID,
                kind: 'profile',
                operation: 'approve',
            });

            if (scopeRes?.success === true && (scopeRes.affectedDivisions?.length > 1 || scopeRes.groupID)) {
                setCrossDivisionScope(scopeRes);
            } else {
                setShowApproveModal(true);
            }
        } catch (scopeErr) {
            console.warn('Cross-division scope check fallback:', scopeErr);
            setShowApproveModal(true);
        } finally {
            setCheckingScope(false);
        }
    };

    const handleConfirmApprove = async (crossDivisionConfirmation = '') => {
        if (busy || !report) return;
        setBusy(true);
        setError(null);

        const pathsArray = Array.from(selectedPaths).sort();

        try {
            const res = await approveFullProfile(
                serverID,
                purgatoryID,
                report.proposalID,
                pathsArray,
                dateChoices,
                typeof crossDivisionConfirmation === 'string' ? crossDivisionConfirmation : ''
            );

            const msg = res?.alreadyApproved === true
                ? 'This selection was already approved. No duplicate changes were made.'
                : 'Profile changes successfully saved. Training and employee access were not altered.';
            setSuccessMessage(msg);
            setReport(null);
            setSelectedPaths(new Set());
            setDateChoices({});
            setShowApproveModal(false);
            setCrossDivisionScope(null);
            setRevertRefreshKey((k) => k + 1);
        } catch (err) {
            console.error('Failed to approve full profile:', err);
            setError(err?.message || 'Failed to approve full profile changes.');
        } finally {
            setBusy(false);
        }
    };

    const hasLicenceChange = useMemo(() => {
        return editableChanges.some(
            (r) => selectedPaths.has(String(r.target)) && r.target === 'driversLicense.licenseNumber'
        );
    }, [editableChanges, selectedPaths]);

    const hasInsuranceChange = useMemo(() => {
        return editableChanges.some(
            (r) => selectedPaths.has(String(r.target)) && r.target === 'carInsurance.file'
        );
    }, [editableChanges, selectedPaths]);

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #edf2f7',
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: '800',
                        color: '#183D44',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <FaUserCheck style={{ color: '#2b6cb0' }} />
                        Review Employee Profile Updates
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#718096' }}>
                        Compare granular candidate values against HA employee records and apply selected fields.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={handleStage}
                    disabled={busy || disabled}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#ffffff',
                        color: '#2b6cb0',
                        border: '1px solid #bee3f8',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: busy || disabled ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                >
                    {busy ? (
                        <>
                            <FaSpinner className="fa-spin" />
                            <span>Working…</span>
                        </>
                    ) : (
                        <>
                            <FaSync />
                            <span>{report ? 'Refresh Comparison' : 'Load Latest Profile Changes'}</span>
                        </>
                    )}
                </button>
            </div>

            {/* Safety Banner */}
            <div style={{
                backgroundColor: '#edf5f4',
                color: '#183D44',
                borderRadius: '6px',
                padding: '10px 14px',
                fontSize: '12.5px',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
            }}>
                <FaShieldAlt style={{ color: '#319795', flexShrink: 0 }} />
                <span>
                    <strong>Isolation Guarantee:</strong> Training reviewed separately · Employment status and account access remain unchanged.
                </span>
            </div>

            {/* Employee Validation Alerts (Live Firestore Listener) */}
            {employeeAlerts.length > 0 && (
                <div style={{
                    backgroundColor: '#fffaf0',
                    border: '1px solid #feebc8',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '12px',
                    color: '#c05621',
                    fontSize: '13px',
                    fontWeight: '600',
                }}>
                    {employeeAlerts.map((msg, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: i < employeeAlerts.length - 1 ? '4px' : 0 }}>
                            <FaExclamationTriangle />
                            <span>{msg}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Profile Revert Controls Component */}
            <ProfileRevertControls
                key={`revert:${serverID}:${purgatoryID}:${revertRefreshKey}`}
                serverID={serverID}
                purgatoryID={purgatoryID}
                enabled={!busy && !disabled}
                onBusy={(b) => setBusy(b)}
                onReverted={() => {
                    setReport(null);
                    setSelectedPaths(new Set());
                    setDateChoices({});
                    setSuccessMessage('Profile changes reverted. Load a new comparison before any further approval.');
                }}
            />

            {/* Status Feedback Messages */}
            {error && (
                <div style={{
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: '#c53030',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                }}>
                    <FaExclamationTriangle style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>{error}</span>
                </div>
            )}

            {successMessage && (
                <div style={{
                    backgroundColor: '#f0fff4',
                    border: '1px solid #c6f6d5',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: '#276749',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <FaCheckCircle style={{ flexShrink: 0 }} />
                    <span>{successMessage}</span>
                </div>
            )}

            {/* Staged Report Section */}
            {report && (
                <div style={{ marginTop: '16px' }}>
                    {/* Metrics Chips */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                        <span style={{
                            backgroundColor: '#edf2f7',
                            color: '#2d3748',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '600',
                        }}>
                            {editableChanges.length} proposed fields
                        </span>
                        <span style={{
                            backgroundColor: '#ebf8ff',
                            color: '#2b6cb0',
                            padding: '4px 12px',
                            borderRadius: '16px',
                            fontSize: '12px',
                            fontWeight: '600',
                        }}>
                            {selectedCount} selected
                        </span>
                        {(attentionItems.length > 0 || issues.length > 0) && (
                            <span style={{
                                backgroundColor: '#fffaf0',
                                color: '#c05621',
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontSize: '12px',
                                fontWeight: '600',
                            }}>
                                {attentionItems.length + issues.length} need attention
                            </span>
                        )}
                    </div>

                    {/* Needs Attention Accordion */}
                    {(attentionItems.length > 0 || issues.length > 0) && (
                        <div style={{
                            border: '1px solid #fbd38d',
                            backgroundColor: '#fffaf0',
                            borderRadius: '8px',
                            marginBottom: '14px',
                            overflow: 'hidden',
                        }}>
                            <button
                                type="button"
                                onClick={() => setOpenDetails((p) => ({ ...p, attention: !p.attention }))}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 14px',
                                    background: 'none',
                                    border: 'none',
                                    color: '#7c2d12',
                                    fontWeight: '700',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <FaExclamationTriangle />
                                    <span>Items Requiring Special Review ({attentionItems.length + issues.length})</span>
                                </span>
                                {openDetails.attention ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                            {openDetails.attention && (
                                <div style={{ padding: '0 14px 12px 14px', fontSize: '12.5px', color: '#7c2d12' }}>
                                    {attentionItems.map((r, i) => (
                                        <div key={i} style={{ padding: '4px 0', borderBottom: '1px solid #feebc8' }}>
                                            <strong>{getFieldLabel(String(r.target))}: </strong>
                                            <span>
                                                {r.type === 'date'
                                                    ? 'Choose intended calendar date below, or keep existing date.'
                                                    : String(r.note)}
                                            </span>
                                        </div>
                                    ))}
                                    {issues.map((r, i) => (
                                        <div key={`issue-${i}`} style={{ padding: '4px 0', color: '#c53030' }}>
                                            <strong>{getFieldLabel(String(r.target))}: </strong>
                                            <span>{r.reason} (This field will not be imported).</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Grouped Field Cards */}
                    {Object.entries(groupedChanges).map(([groupName, rows]) => {
                        const isOpen = openSections[groupName] ?? false;
                        const groupSelectedCount = rows.filter((r) => selectedPaths.has(String(r.target))).length;

                        return (
                            <div key={groupName} style={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                overflow: 'hidden',
                            }}>
                                <button
                                    type="button"
                                    onClick={() => toggleSection(groupName)}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 16px',
                                        backgroundColor: '#f7fafc',
                                        border: 'none',
                                        borderBottom: isOpen ? '1px solid #e2e8f0' : 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div style={{ textAlign: 'left' }}>
                                        <span style={{ fontSize: '14px', fontWeight: '700', color: '#2d3748' }}>
                                            {groupName}
                                        </span>
                                        <span style={{ fontSize: '12px', color: '#718096', marginLeft: '10px' }}>
                                            ({rows.length} proposed · {groupSelectedCount} selected)
                                        </span>
                                    </div>
                                    {isOpen ? <FaChevronUp style={{ color: '#718096' }} /> : <FaChevronDown style={{ color: '#718096' }} />}
                                </button>

                                {isOpen && (
                                    <div style={{ padding: '12px 16px' }}>
                                        {rows.map((row) => {
                                            const path = String(row.target);
                                            const isSelected = selectedPaths.has(path);
                                            const isDate = row.type === 'date';
                                            const choices = row.choices || [];
                                            const note = row.note ? String(row.note) : '';
                                            const isUrl = typeof row.proposed === 'string' && row.proposed.startsWith('https://');

                                            return (
                                                <div key={path} style={{
                                                    border: `1px solid ${isSelected ? '#9ec6bd' : '#e2e8f0'}`,
                                                    backgroundColor: isSelected ? '#fafffd' : '#ffffff',
                                                    borderRadius: '8px',
                                                    padding: '14px',
                                                    marginBottom: '10px',
                                                }}>
                                                    {/* Row Header: Checkbox & Label */}
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                                        <input
                                                            type="checkbox"
                                                            id={`chk-${path}`}
                                                            checked={isSelected}
                                                            onChange={() => handleTogglePath(path)}
                                                            disabled={busy}
                                                            style={{
                                                                width: '18px',
                                                                height: '18px',
                                                                cursor: busy ? 'not-allowed' : 'pointer',
                                                            }}
                                                        />
                                                        <label htmlFor={`chk-${path}`} style={{
                                                            fontSize: '13.5px',
                                                            fontWeight: '700',
                                                            color: '#183D44',
                                                            cursor: busy ? 'not-allowed' : 'pointer',
                                                        }}>
                                                            {getFieldLabel(path)}
                                                        </label>
                                                        <span style={{
                                                            fontSize: '11.5px',
                                                            color: isSelected ? '#2f855a' : '#a0aec0',
                                                            marginLeft: 'auto',
                                                        }}>
                                                            {isSelected ? 'Selected for update' : 'Keep current value'}
                                                        </span>
                                                    </div>

                                                    {/* Values Diff (Current vs Proposed) */}
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                                                        gap: '12px',
                                                    }}>
                                                        {/* Current Value */}
                                                        <div style={{
                                                            backgroundColor: '#f8fafc',
                                                            border: '1px solid #edf2f7',
                                                            borderRadius: '6px',
                                                            padding: '10px 12px',
                                                        }}>
                                                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#718096', textTransform: 'uppercase', marginBottom: '4px' }}>
                                                                Current HA Value
                                                            </div>
                                                            <div style={{ fontSize: '13px', color: '#2d3748', wordBreak: 'break-word' }}>
                                                                {formatValueDisplay(row.current)}
                                                            </div>
                                                        </div>

                                                        {/* Proposed Value */}
                                                        <div style={{
                                                            backgroundColor: '#eff7f4',
                                                            border: '1px solid #c6f6d5',
                                                            borderRadius: '6px',
                                                            padding: '10px 12px',
                                                        }}>
                                                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#2f855a', textTransform: 'uppercase', marginBottom: '4px' }}>
                                                                {isDate ? 'API Date (Choose Below)' : 'Proposed New Value'}
                                                            </div>
                                                            <div style={{ fontSize: '13px', color: '#183D44', wordBreak: 'break-word' }}>
                                                                {isUrl ? (
                                                                    <div>
                                                                        <span style={{ fontWeight: '600' }}>Document link provided:</span>
                                                                        <div style={{ marginTop: '4px' }}>
                                                                            <a
                                                                                href={row.proposed}
                                                                                target="_blank"
                                                                                rel="noopener noreferrer"
                                                                                style={{
                                                                                    color: '#2b6cb0',
                                                                                    display: 'inline-flex',
                                                                                    alignItems: 'center',
                                                                                    gap: '4px',
                                                                                    wordBreak: 'break-all',
                                                                                    fontSize: '12px',
                                                                                }}
                                                                            >
                                                                                <FaExternalLinkAlt /> Open Document
                                                                            </a>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    formatValueDisplay(isDate ? (dateChoices[path] || row.proposed) : row.proposed)
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Note / Warning text if any */}
                                                    {note && (
                                                        <div style={{
                                                            marginTop: '8px',
                                                            fontSize: '12px',
                                                            color: '#c05621',
                                                            backgroundColor: '#fffaf0',
                                                            padding: '6px 10px',
                                                            borderRadius: '4px',
                                                        }}>
                                                            {note}
                                                        </div>
                                                    )}

                                                    {/* Date Selector for Date fields */}
                                                    {isDate && choices.length > 0 && (
                                                        <div style={{ marginTop: '10px' }}>
                                                            <label style={{ fontSize: '12px', fontWeight: '600', color: '#4a5568', display: 'block', marginBottom: '4px' }}>
                                                                Choose intended calendar date:
                                                            </label>
                                                            <select
                                                                value={dateChoices[path] || ''}
                                                                onChange={(e) => handleDateChange(path, e.target.value)}
                                                                disabled={busy}
                                                                style={{
                                                                    width: '100%',
                                                                    padding: '7px 10px',
                                                                    borderRadius: '6px',
                                                                    border: '1px solid #cbd5e0',
                                                                    fontSize: '13px',
                                                                    backgroundColor: '#ffffff',
                                                                }}
                                                            >
                                                                {choices.map((c, idx) => (
                                                                    <option key={idx} value={c.value}>
                                                                        {c.label}: {c.value}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Metadata Toggle */}
                    {editableChanges.some((r) => r.section === 'Provider metadata') && (
                        <div style={{ margin: '14px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input
                                type="checkbox"
                                id="chk-show-metadata"
                                checked={showMetadata}
                                onChange={(e) => setShowMetadata(e.target.checked)}
                                disabled={busy}
                                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                            <label htmlFor="chk-show-metadata" style={{ fontSize: '13px', color: '#4a5568', cursor: 'pointer' }}>
                                Show provider metadata (Agencies, source status, and sync timestamps)
                            </label>
                        </div>
                    )}

                    {/* Collapsible: Unchanged & Skipped Fields */}
                    {(unchanged.length > 0 || skipped.length > 0) && (
                        <div style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            marginBottom: '14px',
                            overflow: 'hidden',
                        }}>
                            <button
                                type="button"
                                onClick={() => setOpenDetails((p) => ({ ...p, unchanged: !p.unchanged }))}
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 14px',
                                    backgroundColor: '#f7fafc',
                                    border: 'none',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#718096',
                                    cursor: 'pointer',
                                }}
                            >
                                <span>Unchanged and Skipped Fields ({unchanged.length + skipped.length})</span>
                                {openDetails.unchanged ? <FaChevronUp /> : <FaChevronDown />}
                            </button>
                            {openDetails.unchanged && (
                                <div style={{ padding: '10px 14px', fontSize: '12.5px', color: '#718096' }}>
                                    {unchanged.map((r, i) => (
                                        <div key={`uc-${i}`} style={{ padding: '3px 0' }}>
                                            • <strong>{getFieldLabel(String(r.target))}</strong>: Matches existing value — no update needed.
                                        </div>
                                    ))}
                                    {skipped.map((r, i) => (
                                        <div key={`sk-${i}`} style={{ padding: '3px 0' }}>
                                            • <strong>{getFieldLabel(String(r.target))}</strong>: Source is empty — existing value preserved.
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Bottom Action Bar */}
                    <div style={{
                        marginTop: '20px',
                        paddingTop: '16px',
                        borderTop: '1px solid #edf2f7',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '12px',
                    }}>
                        <div style={{ fontSize: '13px', color: '#4a5568' }}>
                            <strong>{selectedCount}</strong> profile fields selected, plus verified Onboarding360 identity link.
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            {onReject && (
                                <button
                                    type="button"
                                    onClick={onReject}
                                    disabled={busy || disabled}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: '#ffffff',
                                        color: '#c53030',
                                        border: '1px solid #feb2b2',
                                        padding: '9px 18px',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: busy || disabled ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    <FaTimes />
                                    <span>Reject Proposal</span>
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={handlePreApprove}
                                disabled={busy || disabled || checkingScope}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: '#183D44',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '9px 20px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: busy || disabled || checkingScope ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                }}
                            >
                                    {checkingScope ? (
                                        <>
                                            <FaSpinner className="fa-spin" />
                                            <span>Verifying cross-division scope…</span>
                                        </>
                                    ) : (
                                        <>
                                            <FaCheck />
                                            <span>
                                                {selectedCount === 0
                                                    ? 'Confirm Identity Link Only'
                                                    : `Apply ${selectedCount} Selected Changes`}
                                            </span>
                                        </>
                                    )}
                                </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {showApproveModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '560px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '14px',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#183D44',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <FaCheck /> Approve selected profile changes?
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowApproveModal(false)}
                                disabled={busy}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: '#a0aec0',
                                    cursor: 'pointer',
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{
                            overflowY: 'auto',
                            fontSize: '13px',
                            color: '#4a5568',
                            lineHeight: '1.5',
                            marginBottom: '18px',
                        }}>
                            <p style={{ margin: '0 0 10px 0' }}>
                                Applying <strong>{selectedCount}</strong> selected profile fields, plus the verified Onboarding360 identity link.
                            </p>

                            <div style={{
                                backgroundColor: '#f7fafc',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                padding: '10px 14px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                marginBottom: '12px',
                            }}>
                                {editableChanges
                                    .filter((r) => selectedPaths.has(String(r.target)))
                                    .map((r, i) => {
                                        const p = String(r.target);
                                        const disp = r.type === 'date' ? (dateChoices[p] || r.proposed) : r.proposed;
                                        return (
                                            <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid #edf2f7' }}>
                                                <strong>{getFieldLabel(p)}: </strong>
                                                <span style={{ color: '#2b6cb0' }}>{formatValueDisplay(disp)}</span>
                                            </div>
                                        );
                                    })}
                            </div>

                            {hasLicenceChange && (
                                <div style={{
                                    backgroundColor: '#fffaf0',
                                    border: '1px solid #fbd38d',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    color: '#c05621',
                                    fontSize: '12px',
                                    marginBottom: '8px',
                                }}>
                                    <FaCar style={{ marginRight: '6px' }} />
                                    <strong>Licence update:</strong> Driving licence change will reset DVLA validation and archive previous checks.
                                </div>
                            )}

                            {hasInsuranceChange && (
                                <div style={{
                                    backgroundColor: '#fffaf0',
                                    border: '1px solid #fbd38d',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    color: '#c05621',
                                    fontSize: '12px',
                                    marginBottom: '8px',
                                }}>
                                    <FaShieldAlt style={{ marginRight: '6px' }} />
                                    <strong>Insurance update:</strong> New insurance evidence uploaded requires admin policy validation.
                                </div>
                            )}

                            <p style={{ margin: 0, color: '#718096', fontSize: '12px' }}>
                                Training, employmentStatus, employee access, primary email, and primary telephone remain unchanged.
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                            borderTop: '1px solid #edf2f7',
                            paddingTop: '14px',
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowApproveModal(false)}
                                disabled={busy}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: '1px solid #cbd5e0',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirmApprove}
                                disabled={busy}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#183D44',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {busy ? (
                                    <>
                                        <FaSpinner className="fa-spin" />
                                        <span>Saving…</span>
                                    </>
                                ) : (
                                    <span>Approve Selected Fields</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cross-Division Approval Intelligence Modal */}
            <CrossDivisionScopeModal
                isOpen={Boolean(crossDivisionScope)}
                scope={crossDivisionScope}
                kind="profile"
                operation="approve"
                selectedPaths={Array.from(selectedPaths)}
                dateChoices={dateChoices}
                onConfirm={(groupID) => handleConfirmApprove(groupID)}
                onCancel={() => setCrossDivisionScope(null)}
                busy={busy}
            />
        </div>
    );
};

export default FullProfileReview;
